import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { showNotification } from '@commenda/kakashi';

type NotificationContextProviderProps = {
  children: React.ReactNode;
};

export type NotificationInfo = {
  show: boolean;
  title: string;
  message?: string;
  valid: boolean;
};

type SetNotificationAction =
  | NotificationInfo
  | ((prev: NotificationInfo) => NotificationInfo);

interface NotificationContextType {
  notification: NotificationInfo;
  setNotification: (action: SetNotificationAction) => void;
}

const defaultNotification: NotificationInfo = {
  show: false,
  title: '',
  message: '',
  valid: true,
};

const defaultNotificationContextValue: NotificationContextType = {
  notification: defaultNotification,
  setNotification: () => {},
};

const NotificationContext = createContext<NotificationContextType>(
  defaultNotificationContextValue
);

const NotificationContextProvider = ({
  children,
}: NotificationContextProviderProps) => {
  const lastNotification = useRef<NotificationInfo>(defaultNotification);

  const setNotification = useCallback((action: SetNotificationAction) => {
    const next =
      typeof action === 'function' ? action(lastNotification.current) : action;

    lastNotification.current = next;

    if (next.show && next.title) {
      showNotification({
        title: next.title,
        message: next.message,
        valid: next.valid,
      });
    }
  }, []);

  const contextValues = useMemo(
    () => ({ notification: lastNotification.current, setNotification }),
    [setNotification]
  );

  return (
    <NotificationContext.Provider value={contextValues}>
      {children}
    </NotificationContext.Provider>
  );
};

const useNotification = () => {
  const context = useContext(NotificationContext);

  if (context === undefined || context === null) {
    throw new Error(
      'useNotification must be used within a NotificationContextProvider'
    );
  }

  return context;
};

export { NotificationContextProvider, useNotification };
